import { ToasterContext, ToastType } from '@dexma/ui-components';
import { populateChartLimitValues } from 'adapter/in/dashboards/containers/EditDashboard/WidgetMenu/Panel/Configuration/Tabs/Options/ChartLimit';
import { populateRangesValues } from 'adapter/in/dashboards/containers/EditDashboard/WidgetMenu/Panel/Configuration/Tabs/Options/Range';
import { dispatchCurrentWidgetConfiguration, useCurrentWidgetConfiguration } from 'adapter/in/common/context/currentWidgetConfiguration';
import { dispatchDashboardEdit, useDashboardEdit } from 'adapter/in/dashboards/context/EditDashboard';
import { dispatchWidgetMenuView, useWidgetMenuView, WIDGET_STATUS } from 'adapter/in/dashboards/context/EditDashboard/WidgetMenuView';
import useWidget from 'adapter/in/dashboards/context/Widget/data';
import { dispatchWidgetsView } from 'adapter/in/dashboards/context/Widgets/view';
import { useCase } from 'adapter/in/common/hooks/useCase';
import { getWidgetTypeFieldGroupItem } from 'adapter/in/common/utils/getWidgetTypeFieldGroupItem';
import { JsonChartLimit } from 'adapter/out/client/widgets/data/mappers/common';
import { JsonGaugeRange } from 'adapter/out/client/widgets/data/mappers/GaugeMapper';
import i18n from 'asset/translation';
import { WidgetOptionConfigurationField, WidgetType } from 'domain/entity/widget';
import { isSome } from 'fp-ts/Option';
import { useContext, useEffect } from 'react';
import { useFormContext, UseFormGetValues } from 'react-hook-form';
import { UpdateWidgetCriteriaBuilder } from 'use-case/widget/update/updateWidgetCriteria';
import { CreateWidgetCriteriaBuilder } from 'use-case/widgets/create/createWidgetCriteria';
import { dispatchFormChangesView } from 'adapter/in/common/context/formChanges';
import { ConfigFieldType, FieldStatus, FormFieldData, FormFields, WidgetValues } from 'adapter/in/common/form/types';
import { asValues, getValuesForm } from 'adapter/in/common/form/populateForm';

export type WidgetOptionValues = { key: string; value: string | JsonGaugeRange[] | JsonChartLimit[] };

const getOptionValuesForm = (values: WidgetOptionValues[]) =>
    values.map((value: WidgetOptionValues) => new WidgetOptionConfigurationField(value.key, value.value));

const asInitialValues = ([key, value]: [string, FormFieldData]) => ({
    key,
    value: value.initialValue,
});

export const getCustomOptionsValues = (
    widgetType: WidgetType,
    values: WidgetValues[],
    getValues: UseFormGetValues<FormFields>,
    predicateNonDefaultValues: (configType: ConfigFieldType) => (keyValue: WidgetValues) => boolean
) => {
    switch (widgetType) {
        case WidgetType.GAUGE:
            return getCustomOptionsGauge(values, getValues, predicateNonDefaultValues);
        case WidgetType.BAR:
        case WidgetType.STACKED_BAR:
        case WidgetType.LINE:
            return getCustomOptionsCharts(values, getValues, predicateNonDefaultValues);
        default:
            return values.filter(predicateNonDefaultValues(ConfigFieldType.OPTIONS));
    }
};

export const getCustomOptionsGauge = (
    values: WidgetValues[],
    getValues: UseFormGetValues<FormFields>,
    predicateNonDefaultValues: (configType: ConfigFieldType) => (keyValue: WidgetValues) => boolean
): WidgetOptionValues[] => {
    const ranges = populateRangesValues(getValues);
    const checkpoints = populateChartLimitValues(getValues);
    const defaultOptions = values.filter(predicateNonDefaultValues(ConfigFieldType.OPTIONS));
    return [...defaultOptions, ...ranges, ...checkpoints];
};

export const getCustomOptionsCharts = (
    values: WidgetValues[],
    getValues: UseFormGetValues<FormFields>,
    predicateNonDefaultValues: (configType: ConfigFieldType) => (keyValue: WidgetValues) => boolean
): WidgetOptionValues[] => {
    const checkpoints = populateChartLimitValues(getValues);
    const defaultOptions = values.filter(predicateNonDefaultValues(ConfigFieldType.OPTIONS));
    return [...defaultOptions, ...checkpoints];
};

const useSaveWidgetContainer = (): {
    onSave: () => Promise<void>;
    isLoading: boolean;
    hasError: boolean;
    isSaveButtonDisabled: boolean;
    isSaveButtonShown: boolean;
} => {
    const { status, isSaveButtonShown, isSaveButtonDisabled, tab } = useWidgetMenuView();
    const { widgetType, preset, widgetId } = useCurrentWidgetConfiguration();
    const { setStatus, resetWidgetMenu } = dispatchWidgetMenuView();
    const { createWidget, updateWidget } = useWidget();
    const { dashboard } = useDashboardEdit();
    const { setShowSidePanelMenu } = dispatchDashboardEdit();
    const { reset: resetCurrentWidgetConfiguration } = dispatchCurrentWidgetConfiguration();
    const { setHasChange } = dispatchFormChangesView();
    const { toggleReloadWidgets } = dispatchWidgetsView();
    const toastContext: { toast: (options: { text: string; type: string }) => void } = useContext(ToasterContext);

    const { handleSubmit, getValues } = useFormContext<FormFields>();
    const isCreated = status === WIDGET_STATUS.CREATED;
    const isLoading = status === WIDGET_STATUS.LOADING;
    const isEditing = status === WIDGET_STATUS.EDITING;
    const isUpdated = status === WIDGET_STATUS.UPDATED;

    const { data: dataCreate, error: errorCreate, execute: create } = useCase(createWidget, false);
    const { data: dataUpdate, error: errorUpdate, execute: update } = useCase(updateWidget, false);

    const handleSaveWidget = () => {
        if (widgetType && isSome(dashboard)) {
            setStatus(WIDGET_STATUS.LOADING);
            const values = Object.entries(getValues())
                .filter(([, value]) => !Array.isArray(value))
                .map(asValues);
            const initialValues = Object.entries(getValues()).map(asInitialValues);
            // Use initial values of /data when it's option tab
            const predicateNonDefaultValues = (configType: ConfigFieldType) => (keyValue: WidgetValues) =>
                getValues(keyValue.key).type === configType && getValues(keyValue.key).status !== FieldStatus.DEFAULT_DATA;
            const valuesData =
                tab === ConfigFieldType.OPTIONS
                    ? initialValues.filter(predicateNonDefaultValues(ConfigFieldType.DATA))
                    : values.filter(predicateNonDefaultValues(ConfigFieldType.DATA));
            const valuesOptions: WidgetOptionValues[] = getCustomOptionsValues(widgetType, values, getValues, predicateNonDefaultValues);
            if (isEditing) {
                update(
                    UpdateWidgetCriteriaBuilder.create()
                        .withWidgetId(widgetId)
                        .withType(widgetType)
                        .withPresetKey(preset.key)
                        .withConfigurationFields(getValuesForm(valuesData))
                        .withOptionFields(getOptionValuesForm(valuesOptions))
                        .build()
                );
            } else {
                create(
                    CreateWidgetCriteriaBuilder.create()
                        .withDashboardId(dashboard.value.id)
                        .withName(getWidgetTypeFieldGroupItem(widgetType).tooltip)
                        .withType(widgetType)
                        .withPresetKey(preset.key)
                        .withConfigurationFields(getValuesForm(valuesData))
                        .withOptionFields(getOptionValuesForm(valuesOptions))
                        .build()
                );
            }
        }
    };

    useEffect(() => {
        if (isSome(dataCreate)) {
            setStatus(WIDGET_STATUS.CREATED);
        }
        if (isSome(errorCreate)) setStatus(WIDGET_STATUS.ERROR);
    }, [dataCreate, errorCreate]);

    useEffect(() => {
        if (isSome(dataUpdate)) {
            setStatus(WIDGET_STATUS.UPDATED);
        }
        if (isSome(errorUpdate)) setStatus(WIDGET_STATUS.ERROR);
    }, [dataUpdate, errorUpdate]);

    useEffect(() => {
        if (isCreated || isUpdated) {
            setShowSidePanelMenu(false);
            toggleReloadWidgets(true);
            resetWidgetMenu();
            setHasChange(false);
            resetCurrentWidgetConfiguration();
        }
        const failure = isSome(errorCreate) || isSome(errorUpdate);
        if (failure) {
            toastContext.toast({
                text: i18n.t('dashboards.edit.saveWidgetFailure'),
                type: ToastType.ERROR,
            });
        }
    }, [status]);

    return {
        onSave: handleSubmit(handleSaveWidget),
        isLoading,
        hasError: isSome(errorCreate) || isSome(errorUpdate),
        isSaveButtonDisabled: isLoading || isSaveButtonDisabled,
        isSaveButtonShown,
    };
};

export default useSaveWidgetContainer;
